import {
  GatedFeatureConfigMap,
  GatedFeatureConfigMapData,
  GatedFeatureConfig,
  GatingPeriod,
  GatedFeature,
  SerializableGatedFeatureConfigMap,
} from "@/store/feature-gates/types";
import { GATED_FEATURE_DEFAULTS } from "@/store/feature-gates/defaults";
import { mapValues, merge } from "lodash-es";
import { DateTime } from "luxon";

/**
 * Returns a deep-merged feature gate config map, combining provided config data with defaults.
 *
 * @param featureGateDataOnly - Partial feature gate config data to merge onto defaults.
 * @returns The merged feature gate config map.
 */
export const buildGatedFeatureConfigMapFromData = (
  featureGateDataOnly: GatedFeatureConfigMapData
): GatedFeatureConfigMap => {
  const merged = merge({}, GATED_FEATURE_DEFAULTS, featureGateDataOnly) as GatedFeatureConfigMap;
  return merged;
};

/**
 * Deep-merges the provided feature gate config data onto the default config,
 * returning a new config object. Does not mutate the defaults.
 *
 * @param featureGateDataOnly - Partial config data to merge onto defaults.
 * @returns The merged feature gate configuration.
 */
export const buildFeatureGateConfigFromData = (
  featureConfig: Omit<GatedFeatureConfig, "counter">,
  feature: GatedFeature
): GatedFeatureConfig => {
  const defaultFeatureConfig = GATED_FEATURE_DEFAULTS[feature];
  const merged = merge({}, defaultFeatureConfig, featureConfig);
  return merged;
};

/**
 * Creates a serializable, data-only view of a feature gate config map by
 * removing non-serializable members (e.g., functions like `counter`).
 *
 * The result matches `GatedFeatureConfigMapData` so it can be persisted to
 * cache and later rehydrated via `buildGatedFeatureConfigMapFromData`.
 */
export function buildSerializableGatedFeatureConfigMap(
  map: GatedFeatureConfigMap
): SerializableGatedFeatureConfigMap {
  const cleanedMap: SerializableGatedFeatureConfigMap = mapValues(map, value => {
    const { counter: _counter, ...rest } = value; // get rid of the counter function
    return rest;
  });

  return cleanedMap;
}

export function getPeriodWindow(
  period: GatingPeriod,
  now: DateTime = DateTime.local()
): {
  periodStart: DateTime;
  periodEnd: DateTime;
} {
  switch (period) {
    case GatingPeriod.Monthly: {
      const periodStart = now.startOf("month");
      const periodEnd = now;
      return { periodStart, periodEnd };
    }
    default: {
      const periodStart = now.startOf("month");
      const periodEnd = now;
      return { periodStart, periodEnd };
    }
  }
}
